import { DeleteOutline } from "@mui/icons-material";
import {
  Button,
  Card,
  IconButton,
  LinearProgress,
  Modal,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import React, {
  ChangeEvent,
  DragEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  AUDIO_PRESETS,
  buildAudioThumbnailUrlFromResource,
  isAudioPresetId,
  resolveAudioThumbnail,
  uploadAudioPresetThumbnail,
} from "../../assets/audio-presets";
import { compressImage } from "utils/helpers/compressImage";
import { uploadFileToS3 } from "../UploadField/actions";
import { useStyles } from "./styles";

type Props = {
  open: boolean;
  initialThumbnailPath?: string;
  initialThumbnailName?: string;
  initialResourcePath?: string;
  initialResourceUrl?: string;
  onClose: () => void;
  onSave: (value: {
    thumbnailPath?: string;
    thumbnailName?: string;
  }) => void;
};

export const AudioImageModal = ({
  open,
  initialThumbnailPath,
  initialThumbnailName,
  initialResourcePath,
  initialResourceUrl,
  onClose,
  onSave,
}: Props) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const direction = useTheme().direction;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedPresetId, setSelectedPresetId] = useState<string | undefined>(
    isAudioPresetId(initialThumbnailPath) ? initialThumbnailPath : undefined
  );
  const [customPath, setCustomPath] = useState<string | undefined>(
    !isAudioPresetId(initialThumbnailPath) ? initialThumbnailPath : undefined
  );
  const [customName, setCustomName] = useState<string | undefined>(
    initialThumbnailName
  );
  const [customPreview, setCustomPreview] = useState<string | undefined>(
    undefined
  );
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | undefined>();
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedPresetId(
      isAudioPresetId(initialThumbnailPath) ? initialThumbnailPath : undefined
    );
    setCustomPath(
      !isAudioPresetId(initialThumbnailPath) ? initialThumbnailPath : undefined
    );
    setCustomName(initialThumbnailName);
    setCustomPreview(undefined);
    setError(undefined);
    setProgress(0);
    setUploading(false);
  }, [open, initialThumbnailPath, initialThumbnailName]);

  const customDisplaySrc = useMemo(() => {
    if (customPreview) return customPreview;
    if (customPath && !isAudioPresetId(customPath))
      return resolveAudioThumbnail(
        customPath,
        buildAudioThumbnailUrlFromResource(
          initialResourceUrl,
          initialResourcePath,
          customPath
        )
      );
    return undefined;
  }, [customPath, customPreview, initialResourcePath, initialResourceUrl]);

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError(t("DAILY_ACTIVITY.AUDIO_IMAGE_INVALID"));
      return;
    }
    setError(undefined);
    setUploading(true);
    setProgress(0);
    try {
      const preview = URL.createObjectURL(file);
      setCustomPreview(preview);
      const compressed = await compressImage(file);
      const res = await uploadFileToS3(compressed, (p) => setProgress(p));
      if (res.file) {
        setCustomPath(res.file);
        setCustomName(compressed.name);
        setSelectedPresetId(undefined);
      }
    } catch (e) {
      setError((e as Error)?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = "";
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDeleteUploaded = () => {
    setCustomPath(undefined);
    setCustomName(undefined);
    setCustomPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return undefined;
    });
  };

  const handlePresetClick = (id: string) => {
    setSelectedPresetId(id);
    setCustomPath(undefined);
    setCustomName(undefined);
    setCustomPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return undefined;
    });
  };

  const handleSave = async () => {
    if (customPath && !isAudioPresetId(customPath)) {
      onSave({ thumbnailPath: customPath, thumbnailName: customName });
      onClose();
    } else if (selectedPresetId) {
      setError(undefined);
      setUploading(true);
      setProgress(0);
      try {
        const res = await uploadAudioPresetThumbnail(
          selectedPresetId,
          uploadFileToS3,
          (p) => setProgress(p)
        );
        if (res?.file) {
          onSave({
            thumbnailPath: res.file,
            thumbnailName: `${selectedPresetId}.png`,
          });
          onClose();
        } else {
          setError("Upload failed");
        }
      } catch (e) {
        setError((e as Error)?.message || "Upload failed");
      } finally {
        setUploading(false);
      }
    } else {
      onSave({ thumbnailPath: undefined, thumbnailName: undefined });
      onClose();
    }
  };

  const hasCustom = !!customDisplaySrc;

  return (
    <Modal open={open} onClose={onClose}>
      <Card className={styles.modal} sx={{ direction }}>
        <div className={styles.container}>
          <Typography className={styles.title}>
            {t("DAILY_ACTIVITY.CHANGE_AUDIO_IMAGE")}
          </Typography>

          <div className={styles.uploadSection}>
            <Typography className={styles.sectionLabel}>
              {t("DAILY_ACTIVITY.UPLOADED_IMAGE")}
            </Typography>

            {hasCustom ? (
              <div className={styles.uploadedWrap}>
                <img
                  src={customDisplaySrc}
                  alt="uploaded"
                  className={styles.uploadedImg}
                />
                <IconButton
                  size="small"
                  className={styles.deleteIcon}
                  onClick={handleDeleteUploaded}
                  aria-label="delete"
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </div>
            ) : (
              <div
                className={clsx(
                  styles.dropZone,
                  isDragOver && styles.dropZoneActive
                )}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
              >
                <Typography className={styles.dropPrimary}>
                  {t("DAILY_ACTIVITY.DRAG_DROP_IMAGE")}
                </Typography>
                <Typography className={styles.dropSecondary}>
                  {t("DAILY_ACTIVITY.OR_SELECT_FROM_COLLECTION")}
                </Typography>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className={styles.hiddenInput}
              onChange={handleInputChange}
            />
            {uploading ? (
              <LinearProgress
                variant="determinate"
                value={progress}
                style={{ marginTop: 8 }}
              />
            ) : null}
            {error ? (
              <Typography
                style={{ color: "#F24474", fontSize: 12, marginTop: 6 }}
              >
                {error}
              </Typography>
            ) : null}
          </div>

          <div>
            <Typography className={styles.sectionLabel}>
              {t("DAILY_ACTIVITY.IMAGES_COLLECTION")}
            </Typography>
            <div className={styles.collection}>
              {AUDIO_PRESETS.map((preset) => {
                const selected =
                  !hasCustom && selectedPresetId === preset.id;
                return (
                  <div
                    key={preset.id}
                    className={clsx(
                      styles.tile,
                      selected && styles.tileSelected
                    )}
                    onClick={() => handlePresetClick(preset.id)}
                  >
                    <img
                      src={preset.src}
                      alt={preset.id}
                      className={styles.tileImg}
                    />
                    <span
                      className={clsx(
                        styles.radio,
                        selected && styles.radioSelected
                      )}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.actions}>
            <Button
              className={styles.cancelBtn}
              variant="outlined"
              onClick={onClose}
              disabled={uploading}
            >
              {t("BUTTONS.CANCEL")}
            </Button>
            <Button
              className={styles.saveBtn}
              variant="contained"
              onClick={handleSave}
              disabled={uploading}
            >
              {t("BUTTONS.SAVE")}
            </Button>
          </div>
        </div>
      </Card>
    </Modal>
  );
};
